import React, { useEffect, useState } from 'react';
import { Modal, Badge, Button } from 'react-bootstrap';

import {
  PopupsForAddingState,
  PopupsState,
  RemoveModalDataState,
  ReturnedPortfolio,
} from '../Create-Portfolio/types';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { ReactComponent as LinkIcon } from '../../images/link-45deg.svg';
import { ReactComponent as ArrowIcon } from '../../images/chevron-down.svg';
import { ReactComponent as TrashIcon } from '../../images/trash3-fill.svg';
import '../Preview/Preview.css';
import { ReactComponent as PlusIcon } from '../../images/plus-resume.svg';
import { ReactComponent as FolderIcon } from '../../images/folder-fill.svg';
import { ReactComponent as TrophyIcon } from '../../images/trophy-fill.svg';
import { ReactComponent as AwardsIcon } from '../../images/award-fill.svg';
import { ReactComponent as BuidingIcon } from '../../images/building.svg';

import OverviewPopup from '../Popups/OverviewPopup';
import ProjectsPopup from '../Popups/ProjectsPopup';
import AccomplishmentPopup from '../Popups/AccomplishmentPopup';
import OrganizationsPopup from '../Popups/OrganizationsPopup';
import CertificationsPopup from '../Popups/CertificationsPopup';
import RemoveModal from '../RemoveModal';
import { normalizeUrl } from '../Preview/helpers';
import { toast } from 'react-toastify';
import { persistPortfolioSection, type PortfolioSectionField } from '../../utils/portfolioRefresh';
import { PromptVisibilityIndicator } from '../Prompts/PromptVisibilityIndicator';
import { resolveProjectVisibility } from '../../utils/promptVisibilityMeta';

interface PortfolioTabProps {
  portfolioId: number;
  portfolio: ReturnedPortfolio;
  setPortfolioData: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton: React.Dispatch<React.SetStateAction<boolean>>;
  savePortfolioButton: boolean;
  deletePortfolioContext: boolean;
  getData: (options?: { force?: boolean; silent?: boolean }) => Promise<void>;
}

interface PortfolioItem {
  name: string;
  description?: string;
  date?: string;
  filesUrl?: string[];
  links?: string[];
  startDate?: string;
  endDate?: string;
  link?: string;
  status?: 'Completed' | 'Ongoing';
  visibility?: string;
  isHiddenOnPublic?: boolean;
  is_hidden_on_public?: boolean;
}

const UserProfileTabContent: React.FC<PortfolioTabProps> = ({
  portfolioId,
  portfolio,
  setPortfolioData,
  setSavePortfolioButton,
  deletePortfolioContext,
  savePortfolioButton,
  getData,
}) => {
  const [removeModalData, setRemoveModalData] = useState<RemoveModalDataState | null>(null);
  const [openedPopups, setOpenedPopups] = useState<PopupsState>({
    overview: false,
    projects: portfolio.projects.map(() => false),
    accomplishments: portfolio.accomplishments.map(() => false),
    organizations: portfolio.organizations.map(() => false),
    certifications: portfolio.certifications.map(() => false),
  });
  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>({});
  const toggleCollapse = (section: string) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };
  const [openPopupsToAdd, setPopupsToAdd] = useState<PopupsForAddingState>({
    projects: false,
    accomplishments: false,
    organizations: false,
    certifications: false,
  });

  useEffect(() => {
    setOpenedPopups((prev) => ({
      overview: prev.overview,
      projects: portfolio.projects.map((_, i) => prev.projects[i] ?? false),
      accomplishments: portfolio.accomplishments.map((_, i) => prev.accomplishments[i] ?? false),
      organizations: portfolio.organizations.map((_, i) => prev.organizations[i] ?? false),
      certifications: portfolio.certifications.map((_, i) => prev.certifications[i] ?? false),
    }));
    // Length-only deps: resize popup toggles when sections are added/removed, not on edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portfolio.projects.length,
    portfolio.accomplishments.length,
    portfolio.organizations.length,
    portfolio.certifications.length,
  ]);

  // helper
  const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString() : '');

  const getSectionIcon = (type: string) => {
    const iconClass = `resume-icon ${type}`;
    switch (type) {
      case 'projects':
        return <FolderIcon className={iconClass} />;
      case 'accomplishments':
        return <TrophyIcon className={iconClass} />;
      case 'certifications':
        return <AwardsIcon className={iconClass} />;
      case 'organizations':
        return <BuidingIcon className={iconClass} />;
      default:
        return null;
    }
  };

  // close
  const closeProjectsModal = () =>
    setPopupsToAdd((s) => ({
      ...s,
      projects: false,
      accomplishments: false,
      organizations: false,
      certifications: false,
    }));
  /*   const togglePopup = (popupName: keyof PopupsState) => {
    console.log(openedPopups);
    setOpenedPopups((prev) => ({
      ...prev,
      [popupName]: !prev[popupName],
    }));
    console.log(openedPopups);
  }; */

  const togglePopups = (popupName: keyof PopupsState, index: number) => {
    setOpenedPopups((prev) => {
      const popupArray = prev[popupName];
      if (Array.isArray(popupArray)) {
        const newPopupArray = [...popupArray];
        newPopupArray[index] = !newPopupArray[index];
        return {
          ...prev,
          [popupName]: newPopupArray,
        };
      }
      return prev;
    });
  };

  const handleTogglePopupsForAdding = (field: keyof PopupsForAddingState) => {
    setPopupsToAdd((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCloseModal = () => {
    setRemoveModalData(null);
  };

  const handleDeleteSubsection = async () => {
    if (!removeModalData) return;

    const { field, index } = removeModalData;
    const currentItems = portfolio[field] as ReturnedPortfolio[PortfolioSectionField];
    const updatedItems = [...currentItems];
    updatedItems.splice(index, 1);

    if (!portfolioId) {
      setPortfolioData((prev) => ({ ...prev, [field]: updatedItems }));
      setRemoveModalData(null);
      return;
    }

    try {
      await persistPortfolioSection(
        portfolioId,
        field,
        updatedItems as ReturnedPortfolio[PortfolioSectionField],
        setPortfolioData,
        getData
      );
      setSavePortfolioButton(false);
      toast.success('Item removed');
    } catch (error) {
      console.error('Failed to delete portfolio item:', error);
      toast.error('Could not remove this item. Please try again.');
    } finally {
      setRemoveModalData(null);
    }
  };

  const renderItemCard = (
    item: PortfolioItem,
    type: 'projects' | 'accomplishments' | 'certifications' | 'organizations',
    index: number
  ) => (
    <div className="resume-tile">
      <div className="resume-tile-header">
        <div className="resume-icon-title">
          {getSectionIcon(type)}
          <div>
            <h5>{item.name || '(Untitled)'}</h5>

            {/* meta row under title (only if something exists) */}
            {(item.date || item.startDate || item.endDate || item.status) && (
              <div className="resume-submeta">
                {/* certification = single date */}
                {type !== 'organizations' && item.date && <span>{fmt(item.date)}</span>}

                {/* organizations/projects can show a range if you want */}
                {type === 'organizations' && (item.startDate || item.endDate) && (
                  <span>
                    {(item.startDate ? fmt(item.startDate) : '—') +
                      ' – ' +
                      (item.endDate ? fmt(item.endDate) : 'Present')}
                  </span>
                )}

                {/* optional status chip */}
                {item.status && (
                  <Badge className={`status-chip ${item.status.toLowerCase()}`}>
                    {item.status}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="tile-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            title="Delete"
            className="tile-delete-button"
            onClick={() =>
              setRemoveModalData({
                field: type,
                index,
                // optional: confirmText: `Delete this ${type.slice(0, -1)}? This cannot be undone.`
              })
            }
          >
            <TrashIcon width={16} height={16} />
          </button>

          <button
            title="Click to edit"
            className="tile-edit-button"
            onClick={() => togglePopups(type, index)}
          >
            <EditIcon width={16} height={16} />
          </button>
        </div>
      </div>

      {/* only render body content when it exists */}
      {(item.description ||
        (item.filesUrl && item.filesUrl.length) ||
        (item.links && item.links.length) ||
        item.link) && (
        <>
          <hr className="resume-divider" />
          {item.description && <p className="resume-description">{item.description}</p>}

          <div className="resume-links">
            {item.filesUrl?.map((file, i) => {
              const href = normalizeUrl(file);
              // optional nicer label
              const label = decodeURIComponent(
                String(href).split('?')[0].split('#')[0].replace(/\/+$/, '').split('/').pop() ||
                  'file'
              );
              return (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resume-link"
                >
                  <FolderIcon className="link-icon" /> {label}
                </a>
              );
            })}

            {item.links?.map((link, i) => {
              const href = normalizeUrl(link);
              return (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resume-link"
                >
                  <LinkIcon className="link-icon" /> View Link
                </a>
              );
            })}

            {item.link &&
              (() => {
                const href = normalizeUrl(item.link);
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="resume-link">
                    <LinkIcon className="link-icon" /> View Link
                  </a>
                );
              })()}
          </div>
        </>
      )}

      {type === 'projects' && (
        <div className="project-tab-privacy-row">
          <PromptVisibilityIndicator
            visibility={resolveProjectVisibility(item)}
            itemLabel="project"
            compact
          />
        </div>
      )}

      {portfolio.isProjectsEnabled && (
        <Modal
          show={openedPopups.projects[index]} // Control modal visibility
          onHide={() => togglePopups('projects', index)} // Close the modal
          centered // Center the modal on the screen
          className="ss-app-modal"
          backdropClassName="ss-app-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Project</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ width: '100%' }}>
            <ProjectsPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={index}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              savePortfolioButton={savePortfolioButton}
              popupVariant="editing"
              getData={getData}
            />
          </Modal.Body>
        </Modal>
      )}

      {portfolio.isAccomplishmentsEnabled && (
        <Modal
          show={openedPopups.accomplishments[index]} // Control modal visibility
          onHide={() => togglePopups('accomplishments', index)} // Close the modal
          centered // Center the modal on the screen
          className="ss-app-modal"
          backdropClassName="ss-app-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Accomplishments</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ width: '100%' }}>
            <AccomplishmentPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={index}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              savePortfolioButton={savePortfolioButton}
              popupVariant="editing"
              getData={getData}
            />
          </Modal.Body>
        </Modal>
      )}

      {portfolio.isCertificationsEnabled && (
        <Modal
          show={openedPopups.certifications[index]} // Control modal visibility
          onHide={() => togglePopups('certifications', index)} // Close the modal
          centered // Center the modal on the screen
          className="ss-app-modal"
          backdropClassName="ss-app-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Certifications</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ width: '100%' }}>
            <CertificationsPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={index}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              savePortfolioButton={savePortfolioButton}
              popupVariant="editing"
              getData={getData}
            />
          </Modal.Body>
        </Modal>
      )}

      {portfolio.isOrganizationsEnabled && (
        <Modal
          show={openedPopups.organizations[index]} // Control modal visibility
          onHide={() => togglePopups('organizations', index)} // Close the modal
          centered // Center the modal on the screen
          className="ss-app-modal"
          backdropClassName="ss-app-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Organizations</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ width: '100%' }}>
            <OrganizationsPopup
              portfolio={portfolio}
              portfolioId={portfolioId}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={index}
              setPortfolioData={setPortfolioData}
              getData={getData}
              popupVariant="editing"
            />
          </Modal.Body>
        </Modal>
      )}

      <RemoveModal
        showModal={!!removeModalData}
        onCloseModal={handleCloseModal}
        onSubmit={handleDeleteSubsection}
      />
    </div>
  );

  const renderSection = (
    title: string,
    subtitle: string,
    items: PortfolioItem[],
    type: 'projects' | 'accomplishments' | 'certifications' | 'organizations'
  ) => (
    <div className="resume-section">
      <div className="resume-section-header">
        <div className="resume-header-left">
          <h4>{title}</h4>
          <p className="resume-subtitle">{subtitle}</p>
        </div>

        <div className="resume-header-right">
          <button
            title="Click to edit"
            className="resume-collapse-button"
            onClick={() => toggleCollapse(type)}
          >
            <ArrowIcon
              style={{
                transform: collapsedSections[type] ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
              width={14}
              height={14}
            />{' '}
            {items.length > 0 && items.length}
          </button>
        </div>
      </div>
      {!collapsedSections[type] && items.length > 0 && (
        <div className="resume-tile-list">
          {items.map((item, index) => renderItemCard(item, type, index))}
        </div>
      )}
      <div className="resume-add-button-container">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '1.25rem',
          }}
        >
          <Button className="resume-add-button" onClick={() => handleTogglePopupsForAdding(type)}>
            <PlusIcon width={16} height={16} className="plus-icon" /> Add New {title.slice(0, -1)}
          </Button>
        </div>
      </div>
      {/* Modal for projects  */}
      <Modal
        show={openPopupsToAdd.projects}
        onHide={closeProjectsModal}
        centered
        className="ss-app-modal"
        backdropClassName="ss-app-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ width: '100%' }}>
          {openPopupsToAdd.projects && (
            <ProjectsPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={0}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              popupVariant="adding"
              savePortfolioButton={savePortfolioButton}
              getData={getData}
            />
          )}
        </Modal.Body>
      </Modal>
      {/* Modal for accomplishments  */}
      <Modal
        show={openPopupsToAdd.accomplishments}
        onHide={closeProjectsModal}
        centered
        className="ss-app-modal"
        backdropClassName="ss-app-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Accomplishments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {openPopupsToAdd.accomplishments && (
            <AccomplishmentPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              savePortfolioButton={savePortfolioButton}
              index={0}
              popupVariant="adding"
              getData={getData}
            />
          )}
        </Modal.Body>
      </Modal>
      {/* Modal for certifications  */}
      <Modal
        show={openPopupsToAdd.certifications}
        onHide={closeProjectsModal}
        centered
        className="ss-app-modal"
        backdropClassName="ss-app-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Certifications</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {openPopupsToAdd.certifications && (
            <CertificationsPopup
              portfolioId={portfolioId}
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={0}
              popupVariant="adding"
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
              savePortfolioButton={savePortfolioButton}
              getData={getData}
            />
          )}
        </Modal.Body>
      </Modal>
      {/* Modal for organizations */}
      <Modal
        show={openPopupsToAdd.organizations}
        onHide={closeProjectsModal}
        centered
        className="ss-app-modal"
        backdropClassName="ss-app-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {openPopupsToAdd.organizations && (
            <OrganizationsPopup
              portfolio={portfolio}
              portfolioId={portfolioId}
              setOpenedPopups={setOpenedPopups}
              setPopupsToAdd={setPopupsToAdd}
              index={0}
              popupVariant="adding"
              setPortfolioData={setPortfolioData}
              getData={getData}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );

  return (
    <>
      {portfolio.isOverviewEnabled && (
        <div className="resume-wrapper">
          <div className="resume-section">
            <div className="resume-section-header">
              <div className="resume-header-left">
                <h4>Overview</h4>
                <p className="resume-subtitle">Give a brief summary about yourself</p>
              </div>
              <div className="resume-header-right">
                <button
                  title="Click to edit"
                  className="resume-collapse-button"
                  onClick={() => setOpenedPopups((prev) => ({ ...prev, overview: true }))}
                >
                  <EditIcon width={16} height={16} />
                </button>
              </div>
            </div>

            <div className="resume-tile-list">
              <div className="resume-tile">
                <div className="resume-description">
                  {portfolio.overview || 'No overview provided yet.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {portfolio.isOverviewEnabled && openedPopups.overview && (
        <Modal
          show={openedPopups.overview}
          onHide={() => setOpenedPopups((prev) => ({ ...prev, overview: false }))}
          centered
          backdrop="static"
          size="lg"
          className="ss-app-modal"
          backdropClassName="ss-app-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Overview</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ width: '100%' }}>
            <OverviewPopup
              portfolio={portfolio}
              setOpenedPopups={setOpenedPopups}
              setPortfolioData={setPortfolioData}
              setSavePortfolioButton={setSavePortfolioButton}
            />
          </Modal.Body>
        </Modal>
      )}

      <div className="resume-wrapper">
        {/* {portfolio.isOverviewEnabled && (
          <div className="resume-section">
            <div className="resume-section-header">
              <h4>Overview</h4>
              <button
                className="tile-edit-button"
                title="edit overview"
                onClick={() =>
                  setOpenedPopups((prev) => ({ ...prev, overview: true }))
                }
              >
                <EditIcon width={16} height={16} />
              </button>
            </div>
            <p className="resume-description">{portfolio.overview}</p>
          </div>
        )} */}

        {portfolio.isProjectsEnabled &&
          renderSection(
            'Projects',
            'Highlight major work experiences or collaborations',
            portfolio.projects,
            'projects'
          )}

        {portfolio.isAccomplishmentsEnabled &&
          renderSection(
            'Accomplishments',
            'Showcase notable achievements or milestones',
            portfolio.accomplishments,
            'accomplishments'
          )}

        {portfolio.isCertificationsEnabled &&
          renderSection(
            'Certifications',
            'Showcase achievements that stand out in your career or education.',
            portfolio.certifications.map((cert: any) => ({
              ...cert,
              filesUrl: Array.isArray(cert.filesUrl)
                ? cert.filesUrl.map((f: any) => (typeof f === 'string' ? f : f.name))
                : [],
            })),
            'certifications'
          )}

        {portfolio.isOrganizationsEnabled &&
          renderSection(
            'Organizations',
            'Show memberships, volunteer work or leadership roles.',
            portfolio.organizations,
            'organizations'
          )}
      </div>
    </>
  );
};

export default UserProfileTabContent;
